import {
	Component,
	ViewEncapsulation,
	ChangeDetectorRef,
	ViewChild,
	ElementRef,
	ChangeDetectionStrategy,
	HostBinding,
	EventEmitter
} from '@angular/core';
import { GridApi, IHeaderParams, PaginationChangedEvent, SelectionChangedEvent } from '@ag-grid-community/core';
import { IHeaderAngularComp } from '@ag-grid-community/angular';
import { firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgClass } from '@angular/common';

import { DatacenterSelectionService } from '../../services/datacenter-selection.service';

interface MyParams extends IHeaderParams {
	showCheckbox: boolean;
	onDataGridReady?: EventEmitter<GridApi>;
}

@Component({
	selector: 'copo-checkbox-header',
	templateUrl: './checkbox-header.component.html',
	styleUrl: './checkbox-header.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NgIf,
		MatIconModule,
		NgClass,
		TranslateModule,
	],
})
export class CheckboxHeaderComponent implements IHeaderAngularComp {
	@HostBinding('class.copo-checkbox-header') header = true;
	@ViewChild('menuButton', { read: ElementRef }) menuButton!: ElementRef<HTMLElement>;
	params?: MyParams;
	allSelected: 'checked' | 'indeterminate' | 'unchecked' = 'unchecked';
	remoteRowsSelected = false;
	sortingAscending = false;
	sortingDescending = false;
	dataGrid?: GridApi;
	oldRowCount = 0;
	constructor(
		readonly changeDetectorRef: ChangeDetectorRef,
		private readonly selectionService: DatacenterSelectionService,
	) {}

	agInit(params: MyParams) {
		this.params = params;
		const { headerTooltip } = params.column.getColDef();
		if (headerTooltip) {
			params.eGridHeader.ariaLabel = headerTooltip;
		}
		this.updateSorting(params);
		if (params.onDataGridReady) {
			firstValueFrom(params.onDataGridReady).then(datagrid => {
				this.dataGrid = datagrid;
				this.dataGrid.addEventListener('selectionChanged', (event?: SelectionChangedEvent<unknown>) => {
					if (event && event.source === 'api') {
						return;
					}
					let allSelected = true;
					let allUnselected = true;
					this.dataGrid?.forEachNode(node => {
						if (!node.isSelected()) {
							allSelected = false;
						} else {
							allUnselected = false;
						}
					});
					const lastPage = this.dataGrid?.paginationIsLastPageFound();
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
					this.allSelected = allUnselected && (!this.remoteRowsSelected || lastPage) ? 'unchecked'
						// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
						: allSelected && (this.remoteRowsSelected || lastPage)
							? 'checked' : 'indeterminate';
					this.changeDetectorRef.markForCheck();
				});
			}).catch((err: unknown) => {
				console.error('Error', err);
			});
		}
		params.column.addEventListener('sortChanged', () => {
			this.updateSorting(params);
		});
		params.column.addEventListener('filterChanged', () => {
			this.changeDetectorRef.markForCheck();
		});
		let lastRowCount = 0;
		params.api.addEventListener('paginationChanged', (ev: PaginationChangedEvent) => {
			this.oldRowCount = lastRowCount;
			lastRowCount = ev.api.getDisplayedRowCount();
			if (!this.remoteRowsSelected) {
				return;
			}
			ev.api.forEachNode(node => {
				if (node.rowIndex !== null && node.rowIndex < this.oldRowCount - 1) {
					return;
				}
				node.setSelected(true, false);
			});
		});
	}

	updateSorting(params: MyParams) {
		const sort = params.column.getSort();
		this.sortingAscending = sort === 'asc';
		this.sortingDescending = sort === 'desc';
		this.changeDetectorRef.markForCheck();
	}

	refresh(params: MyParams) {
		this.agInit(params);
		return true;
	}

	onMenuClicked() {
		if (this.params) {
			this.params.showColumnMenu(this.menuButton.nativeElement);
		}
	}

	onSortRequested(event: MouseEvent | TouchEvent) {
		if (!this.params) {
			return;
		}
		if (!this.params.enableSorting) {
			return;
		}
		this.params.progressSort(event.shiftKey);
		this.changeDetectorRef.markForCheck();
	}

	select(event: MouseEvent | TouchEvent) {
		this.selectionService.isAllSelected = true;
		this.selectionService.deselectedNodes = [];
		this.selectionService.selectedNodes = [];
		event.stopPropagation();
		if (!this.params) {
			return;
		}
		this.allSelected = 'checked';
		this.remoteRowsSelected = true;
		this.params.api.forEachNode(node => {
			node.setSelected(true, false);
		});
		if (this.dataGrid) {
			this.dataGrid.forEachNode(node => {
				node.setSelected(true, false);
			});
		}
		this.changeDetectorRef.markForCheck();
	}

	unselect(event: MouseEvent | TouchEvent) {
		this.selectionService.isAllSelected = false;
		this.selectionService.deselectedNodes = [];
		this.selectionService.selectedNodes = [];
		event.stopPropagation();
		if (!this.params) {
			return;
		}
		this.allSelected = 'unchecked';
		this.remoteRowsSelected = false;
		this.params.api.forEachNode(node => {
			node.setSelected(false, false);
		});
		if (this.dataGrid) {
			this.dataGrid.forEachNode(node => {
				node.setSelected(false, false);
			});
		}
		this.changeDetectorRef.markForCheck();
	}

	headerAlignement() {
		return this.params?.column.getColDef().type === 'numericColumn';
	}
}
